import { CRACKMAP_WFS_BASE_URL } from '../crackmaps/crackmapWfs';

export type WfsLonLatBbox = {
  readonly west: number;
  readonly south: number;
  readonly east: number;
  readonly north: number;
};

export type WebMercatorExtent = {
  readonly xmin: number;
  readonly ymin: number;
  readonly xmax: number;
  readonly ymax: number;
  readonly spatialReference: { readonly wkid: 3857 };
};

const WEB_MERCATOR_HALF_WIDTH = 20037508.34;

export function lonLatToWebMercator(
  lon: number,
  lat: number,
): { readonly x: number; readonly y: number } {
  const clampedLat = Math.max(-85.05112878, Math.min(85.05112878, lat));
  const x = (lon * WEB_MERCATOR_HALF_WIDTH) / 180;
  const latRad = (clampedLat * Math.PI) / 180;
  const y =
    (Math.log(Math.tan(Math.PI / 4 + latRad / 2)) * WEB_MERCATOR_HALF_WIDTH) /
    Math.PI;
  return { x, y };
}

export function lonLatBboxToWebMercatorExtent(
  bbox: WfsLonLatBbox,
): WebMercatorExtent {
  const sw = lonLatToWebMercator(bbox.west, bbox.south);
  const ne = lonLatToWebMercator(bbox.east, bbox.north);
  return {
    xmin: Math.min(sw.x, ne.x),
    ymin: Math.min(sw.y, ne.y),
    xmax: Math.max(sw.x, ne.x),
    ymax: Math.max(sw.y, ne.y),
    spatialReference: { wkid: 3857 },
  };
}

export function resolveWfsTypeName(wfsUrlOrTypeName: string): string | null {
  const trimmed = wfsUrlOrTypeName.trim();
  if (!trimmed) {
    return null;
  }
  if (!/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  try {
    const url = new URL(trimmed);
    return (
      url.searchParams.get('typeName') ||
      url.searchParams.get('typeNames') ||
      url.searchParams.get('TYPENAME') ||
      url.searchParams.get('TYPENAMES')
    );
  } catch {
    return null;
  }
}

export function buildWfsGetCapabilitiesUrl(wfsUrlOrTypeName: string): string {
  const trimmed = wfsUrlOrTypeName.trim();
  const isFullUrl = /^https?:\/\//i.test(trimmed);
  const url = isFullUrl ? new URL(trimmed) : new URL(CRACKMAP_WFS_BASE_URL);
  url.searchParams.set('service', 'WFS');
  url.searchParams.set('version', '1.1.0');
  url.searchParams.set('request', 'GetCapabilities');
  url.searchParams.delete('typeName');
  url.searchParams.delete('typeNames');
  url.searchParams.delete('outputFormat');
  url.searchParams.delete('bbox');
  url.searchParams.delete('srsname');
  url.searchParams.delete('propertyName');
  url.searchParams.delete('maxFeatures');
  url.searchParams.delete('count');
  return url.toString();
}

function readElementText(parent: Element, localName: string): string | null {
  const nodes = parent.getElementsByTagName('*');
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes.item(index);
    if (!node || node.localName !== localName) {
      continue;
    }
    const text = node.textContent?.trim();
    if (text) {
      return text;
    }
  }
  return null;
}

function parseBoundingBoxElement(element: Element): WfsLonLatBbox | null {
  const lower = readElementText(element, 'LowerCorner');
  const upper = readElementText(element, 'UpperCorner');
  if (lower && upper) {
    const lowerParts = lower.split(/\s+/).map(Number);
    const upperParts = upper.split(/\s+/).map(Number);
    if (
      lowerParts.length >= 2 &&
      upperParts.length >= 2 &&
      lowerParts.every(Number.isFinite) &&
      upperParts.every(Number.isFinite)
    ) {
      return {
        west: Math.min(lowerParts[0]!, upperParts[0]!),
        south: Math.min(lowerParts[1]!, upperParts[1]!),
        east: Math.max(lowerParts[0]!, upperParts[0]!),
        north: Math.max(lowerParts[1]!, upperParts[1]!),
      };
    }
  }

  const minx = element.getAttribute('minx');
  const miny = element.getAttribute('miny');
  const maxx = element.getAttribute('maxx');
  const maxy = element.getAttribute('maxy');
  if (minx && miny && maxx && maxy) {
    const west = Number(minx);
    const south = Number(miny);
    const east = Number(maxx);
    const north = Number(maxy);
    if ([west, south, east, north].every(Number.isFinite)) {
      return { west, south, east, north };
    }
  }

  return null;
}


export function parseWfsFeatureTypeLonLatBbox(
  capabilitiesXml: string,
  typeName: string,
): WfsLonLatBbox | null {
  const doc = new DOMParser().parseFromString(capabilitiesXml, 'text/xml');
  if (doc.querySelector('parsererror')) {
    return null;
  }

  const normalizedType = typeName.trim().toLowerCase();
  const featureTypes = Array.from(doc.getElementsByTagName('*')).filter(
    (node) => node.localName === 'FeatureType',
  );

  for (const featureType of featureTypes) {
    const name = readElementText(featureType, 'Name');
    if (!name || name.trim().toLowerCase() !== normalizedType) {
      continue;
    }

    const bboxCandidates = Array.from(featureType.getElementsByTagName('*')).filter(
      (node) =>
        node.localName === 'WGS84BoundingBox' ||
        node.localName === 'LatLongBoundingBox' ||
        node.localName === 'BoundingBox',
    );

    for (const candidate of bboxCandidates) {
      const bbox = parseBoundingBoxElement(candidate);
      if (bbox) {
        return bbox;
      }
    }
  }

  return null;
}


export async function fetchWfsFeatureTypeWebMercatorExtent(
  wfsUrlOrTypeName: string,
  signal?: AbortSignal,
): Promise<WebMercatorExtent | null> {
  const typeName = resolveWfsTypeName(wfsUrlOrTypeName);
  if (!typeName) {
    return null;
  }

  const capabilitiesUrl = buildWfsGetCapabilitiesUrl(wfsUrlOrTypeName);
  try {
    const response = await fetch(capabilitiesUrl, { signal });
    if (!response.ok) {
      return null;
    }
    const xml = await response.text();
    const lonLat = parseWfsFeatureTypeLonLatBbox(xml, typeName);
    if (!lonLat) {
      return null;
    }
    return lonLatBboxToWebMercatorExtent(lonLat);
  } catch {
    return null;
  }
}
